import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaSearch, FaArrowRight, FaMapMarkerAlt, FaStar, FaShieldAlt, FaClock, FaCheckCircle, FaLayerGroup } from 'react-icons/fa';
import apiClient from '../api/api';
import loadImage from '../assets/imageLoader';

const HomePage = ({ workers, workersLoading, workersError }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [industries, setIndustries] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(true);

  useEffect(() => {
    const fetchIndustries = async () => {
        try {
            setLoadingIndustries(true);
            const res = await apiClient.get('/categories?parentsOnly=true');
            setIndustries(res.data.slice(0, 4) || []); // Show top 4
        } catch (err) {
            console.error("Failed to load industries");
        } finally {
            setLoadingIndustries(false);
        }
    };
    fetchIndustries();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${searchTerm.trim()}`);
    }
  };

  const renderIcon = (icon, size = 'text-3xl') => {
    if (!icon) return <FaLayerGroup className={size} />;
    if (icon.startsWith('http') || icon.startsWith('/uploads')) {
        return <img src={loadImage(icon)} alt="Icon" className="w-full h-full object-cover" />;
    }
    return <span className={size}>{icon}</span>;
  };

  const ServiceCard = ({ industry }) => (
      <div 
        className="bg-white p-8 rounded-[2rem] border border-slate-100 hover:border-emerald-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group cursor-pointer flex flex-col h-full" 
        onClick={() => navigate('/categories')}
      >
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 bg-slate-50 text-slate-400 shadow-inner group-hover:bg-emerald-50 group-hover:text-emerald-600 transition-all overflow-hidden`}>
              {renderIcon(industry.Icon)}
          </div>
          <h3 className="text-xl font-black text-slate-800 mb-2 group-hover:text-emerald-700 transition-colors">{industry.Name}</h3>
          <p className="text-slate-500 leading-relaxed text-sm line-clamp-2">{industry.Description}</p>
          <div className="mt-auto pt-6 flex items-center text-xs font-black text-emerald-600 uppercase tracking-widest gap-2 opacity-0 group-hover:opacity-100 transition-all">
              Explore Services <FaArrowRight size={10} />
          </div>
      </div>
  );

  const WorkerCard = ({ worker }) => (
      <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 hover:shadow-lg transition-all group relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-br from-slate-100 to-slate-200"></div>
          <div className="relative flex flex-col items-center -mt-12 pt-12">
              <img 
                  src={loadImage(worker.profile?.picture)} 
                  alt={worker.profile?.name} 
                  className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow-md bg-white"
              />
              <div className="mt-4 text-center px-2">
                  <h4 className="text-lg font-black text-slate-800 truncate w-full">{worker.profile?.name || 'Worker'}</h4>
                  <p className="text-emerald-600 font-bold text-[10px] uppercase tracking-wider bg-emerald-50 px-3 py-1 rounded-full inline-block mt-1">
                      {worker.categories?.[0]?.Name || 'General Worker'}
                  </p>
              </div>
              <div className="mt-4 w-full flex justify-between border-t border-slate-50 pt-4 text-sm font-bold text-slate-500">
                  <span className="flex items-center"><FaStar className="text-yellow-400 mr-1" /> {worker.reliability_score || 100}%</span>
                  <span className="flex items-center"><FaMapMarkerAlt className="mr-1 text-slate-300" /> {worker.profile?.location?.district || 'BD'}</span>
              </div>
              <Link to={`/workers/profile/${worker.profileId}`} className="mt-6 w-full py-3 bg-slate-900 text-white rounded-xl font-bold text-center group-hover:bg-emerald-600 transition-colors">
                  View Profile
              </Link>
          </div>
      </div>
  );

  return (
    <div className="font-sans">
      
      {/* --- Hero Section --- */}
      <section className="relative bg-slate-900 text-white pt-32 pb-24 lg:pt-48 lg:pb-40 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=2000&auto=format&fit=crop')] bg-cover bg-center opacity-20 grayscale"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent"></div>
          
          <div className="container mx-auto px-6 relative z-10 text-center">
              <span className="text-emerald-400 font-black tracking-widest uppercase text-xs mb-4 block animate-fade-in-up">Professional Labor Marketplace</span>
              <h1 className="text-5xl lg:text-7xl font-black mb-6 leading-tight max-w-4xl mx-auto animate-fade-in-up">
                  Hire Top Experts <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300 font-serif italic">In Seconds.</span>
              </h1>
              <p className="text-slate-300 text-lg lg:text-xl mb-10 max-w-2xl mx-auto leading-relaxed animate-fade-in-up delay-100">
                  Connect with verified professionals for your home, business, and agricultural needs across Bangladesh. Secure payments and live tracking included.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="max-w-2xl mx-auto relative group animate-fade-in-up delay-200">
                  <input 
                      type="text" 
                      placeholder="Try 'Electrician' or 'Rice Harvester'..." 
                      className="w-full py-5 pl-8 pr-36 rounded-full text-slate-800 font-medium focus:ring-4 ring-emerald-500/30 outline-none shadow-2xl"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button type="submit" className="absolute right-2 top-2 bottom-2 bg-emerald-600 text-white px-8 rounded-full font-bold hover:bg-emerald-500 transition-all flex items-center">
                      Find Help <FaSearch className="ml-2" />
                  </button>
              </form>

              {/* Stats */}
              <div className="mt-16 flex flex-wrap justify-center gap-8 lg:gap-16 text-slate-400 font-bold text-sm uppercase tracking-wider animate-fade-in-up delay-300">
                  <div className="flex items-center"><span className="text-3xl text-white mr-2 font-black">24/7</span> Availability</div>
                  <div className="flex items-center"><span className="text-3xl text-white mr-2 font-black">Verified</span> IDs</div>
                  <div className="flex items-center"><span className="text-3xl text-white mr-2 font-black">Secure</span> Escrow</div>
              </div>
          </div>
      </section>

      {/* --- Categories Section --- */}
      <section className="py-24 bg-slate-50">
          <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                  <h2 className="text-3xl font-black text-slate-800 mb-4 tracking-tight">Browse by Industry</h2>
                  <p className="text-slate-500 max-w-xl mx-auto font-medium">Whatever you need, we have a specialist ready to help. Explore our main service categories.</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {loadingIndustries ? (
                      [1,2,3,4].map(i => <div key={i} className="h-64 bg-white rounded-[2rem] animate-pulse"></div>)
                  ) : industries.length > 0 ? (
                      industries.map(industry => <ServiceCard key={industry.id} industry={industry} />)
                  ) : (
                      <p className="text-center col-span-full text-slate-400">Loading industries...</p>
                  )}
              </div>
              
              <div className="text-center mt-12">
                  <Link to="/categories" className="inline-flex items-center font-black text-slate-800 border-b-2 border-emerald-500 hover:text-emerald-600 transition-colors uppercase text-xs tracking-widest pb-1">
                      View All 20+ Categories <FaArrowRight className="ml-2 text-[10px]" />
                  </Link>
              </div>
          </div>
      </section>

      {/* --- Featured Workers --- */}
      <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
              <div className="flex flex-col md:flex-row justify-between items-end mb-12">
                  <div>
                      <h2 className="text-3xl font-black text-slate-800 mb-2 tracking-tight">Top Professionals</h2>
                      <p className="text-slate-500 font-medium">Verified experts with the highest reliability scores.</p>
                  </div>
                  <Link to="/search" className="hidden md:block bg-slate-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors shadow-lg shadow-slate-200">See All Experts</Link>
              </div>

              {workersLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                      {[1,2,3,4].map(i => <div key={i} className="h-80 bg-slate-100 rounded-3xl animate-pulse"></div>)}
                  </div>
              ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                      {workers && workers.length > 0 ? workers.slice(0, 4).map(worker => (
                          <WorkerCard key={worker.id} worker={worker} />
                      )) : (
                          <p className="text-slate-400 col-span-full text-center py-10 italic">Looking for available professionals in your area...</p>
                      )}
                  </div>
              )}
              
              <div className="mt-8 text-center md:hidden">
                  <Link to="/search" className="bg-slate-100 text-slate-700 px-6 py-3 rounded-xl font-bold hover:bg-slate-200 transition-colors">See All Professionals</Link>
              </div>
          </div>
      </section>

      {/* --- Trust Section --- */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
              <div className="text-center mb-20">
                  <h2 className="text-3xl font-black mb-4 tracking-tight">Why Service24?</h2>
                  <p className="text-slate-400 font-medium">The most trusted platform for on-demand labor in Bangladesh.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                  <div className="p-8 bg-slate-800/50 rounded-[2.5rem] border border-slate-800 hover:border-emerald-500/30 transition-colors">
                      <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-500 mb-6">
                          <FaShieldAlt size={24} />
                      </div>
                      <h3 className="text-xl font-bold mb-3">ID Verification</h3>
                      <p className="text-slate-400 text-sm leading-relaxed">Every professional must submit NID/Passport for admin review before they can start working.</p>
                  </div>
                  <div className="p-8 bg-slate-800/50 rounded-[2.5rem] border border-slate-800 hover:border-blue-500/30 transition-colors">
                      <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center text-blue-500 mb-6">
                          <FaCheckCircle size={24} />
                      </div>
                      <h3 className="text-xl font-bold mb-3">Reliability Scoring</h3>
                      <p className="text-slate-400 text-sm leading-relaxed">Our smart algorithm tracks arrival times and completion rates to give you the most dependable workers.</p>
                  </div>
                  <div className="p-8 bg-slate-800/50 rounded-[2.5rem] border border-slate-800 hover:border-purple-500/30 transition-colors">
                      <div className={`w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center text-purple-500 mb-6`}>
                          <FaClock size={24} />
                      </div>
                      <h3 className="text-xl font-bold mb-3">Escrow Security</h3>
                      <p className="text-slate-400 text-sm leading-relaxed">Funds are held safely in escrow and only released to the worker once you confirm the job is finished.</p>
                  </div>
              </div>
          </div>
      </section>

      {/* --- CTA Section --- */}
      <section className="py-20 bg-emerald-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
          <div className="container mx-auto px-6 text-center relative z-10">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tight">Ready to get started?</h2>
              <p className="text-emerald-100 text-lg mb-10 max-w-2xl mx-auto font-medium">Join Service24 today and connect with thousands of experts or find your next professional opportunity.</p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Link to="/register" className="bg-white text-emerald-700 px-10 py-4 rounded-2xl font-black hover:bg-slate-50 transition-all hover:-translate-y-1 shadow-xl">Join Service24</Link>
                  <Link to="/jobs" className="bg-emerald-700 text-white px-10 py-4 rounded-2xl font-black hover:bg-emerald-800 transition-all hover:-translate-y-1 shadow-xl border border-emerald-500">Hire an Expert</Link>
              </div>
          </div>
      </section>

    </div>
  );
};

export default HomePage;
